import React, { useCallback, useState } from 'react';
import { Box, Menu, MenuItem, Typography } from '@mui/material';
import { serviceTypes, TypeServiceEnum } from 'features/salesOrder/types';

const MENU_ID = 'add-service-type-menu';

type MenuAnchorOrigin = {
	vertical: 'top' | 'center' | 'bottom';
	horizontal: 'left' | 'center' | 'right';
};

type UseServiceTypeMenuOptions = {
	onSelect: (serviceType: TypeServiceEnum) => void;
	triggerId?: string;
	menuAnchorOrigin?: MenuAnchorOrigin;
};

type UseServiceTypeMenuReturn = {
	handleOpen: (event: React.MouseEvent<HTMLElement>) => void;
	menu: React.ReactElement;
	open: boolean;
	menuId: string;
};

export const useServiceTypeMenu = ({
	onSelect,
	triggerId = 'add-service-button',
	menuAnchorOrigin = { vertical: 'top', horizontal: 'center' },
}: UseServiceTypeMenuOptions): UseServiceTypeMenuReturn => {
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const open = Boolean(anchorEl);

	const handleOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	}, []);

	const handleClose = useCallback(() => {
		setAnchorEl(null);
	}, []);

	const handleSelect = useCallback(
		(serviceType: TypeServiceEnum) => {
			handleClose();
			onSelect(serviceType);
		},
		[handleClose, onSelect],
	);

	const menu = (
		<Menu
			id={MENU_ID}
			anchorEl={anchorEl}
			open={open}
			onClose={handleClose}
			disableRestoreFocus
			anchorOrigin={menuAnchorOrigin}
			transformOrigin={{
				vertical: menuAnchorOrigin.vertical === 'top' ? 'bottom' : 'top',
				horizontal: menuAnchorOrigin.horizontal,
			}}
			MenuListProps={{
				'aria-labelledby': triggerId,
			}}
		>
			{serviceTypes.map((serviceType) => (
				<MenuItem key={serviceType.id} onClick={() => handleSelect(serviceType.id)}>
					<Box display="flex" gap={1} alignItems="center">
						{serviceType.icon}
						<Typography color="text.primary">{serviceType.name}</Typography>
					</Box>
				</MenuItem>
			))}
		</Menu>
	);

	return {
		handleOpen,
		menu,
		open,
		menuId: MENU_ID,
	};
};
